import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

interface WhatsAppConfig {
  adminWhatsApp: string;
  callMeBotApiKey: string;
  customerNotificationsEnabled: boolean;
  adminNotificationsEnabled: boolean;
  storeName: string;
}

const DEFAULT_CONFIG: WhatsAppConfig = {
  adminWhatsApp: "966541138847",
  callMeBotApiKey: "",
  customerNotificationsEnabled: true,
  adminNotificationsEnabled: true,
  storeName: "المتجر",
};

const DEFAULT_OTP_TEMPLATE = `🔐 رمز التحقق الخاص بك في {storeName}:\n\n*{otp}*\n\nصالح لمدة 10 دقائق — لا تشاركه مع أحد.`;

interface WhatsAppSettingsProps {
  onToast: (msg: string) => void;
}

const STATUS_MESSAGES: Record<string, { label: string; icon: string; color: string; defaultMsg: string }> = {
  pending:   { label: "استلام الطلب",  icon: "ri-shopping-bag-3-line", color: "text-amber-600",  defaultMsg: "مرحباً {name}، تم استلام طلبك #{orderRef} بنجاح! سنتواصل معك قريباً." },
  confirmed: { label: "تأكيد الطلب",  icon: "ri-checkbox-circle-line", color: "text-green-600",  defaultMsg: "تم تأكيد طلبك #{orderRef} {name}! 🎉 سيتم شحنه خلال 24 ساعة." },
  shipped:   { label: "شحن الطلب",    icon: "ri-send-plane-line",      color: "text-sky-600",    defaultMsg: "طلبك #{orderRef} في الطريق إليك {name}! 🚚 سيصلك قريباً." },
  delivered: { label: "تم التوصيل",   icon: "ri-truck-line",           color: "text-teal-600",   defaultMsg: "تم توصيل طلبك #{orderRef} بنجاح {name}! ✅ نتمنى أن ينال إعجابك." },
  cancelled: { label: "إلغاء الطلب",  icon: "ri-close-circle-line",    color: "text-red-600",    defaultMsg: "نأسف، تم إلغاء طلبك #{orderRef}. للاستفسار تواصل معنا." },
};

export default function WhatsAppSettings({ onToast }: WhatsAppSettingsProps) {
  const [config, setConfig] = useState<WhatsAppConfig>(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"setup" | "messages">("setup");
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingStatus, setTestingStatus] = useState<string | null>(null);
  const [customMessages, setCustomMessages] = useState<Record<string, string>>({});
  const [otpTemplate, setOtpTemplate] = useState("");
  const [testingOtp, setTestingOtp] = useState(false);
  const [otpPreview, setOtpPreview] = useState(false);
  const [newCustomerTemplate, setNewCustomerTemplate] = useState("");
  const [testingNewCustomer, setTestingNewCustomer] = useState(false);
  const [newCustomerPreview, setNewCustomerPreview] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("whatsapp_settings");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConfig((prev) => ({ ...prev, ...parsed }));
      } catch { /* ignore */ }
    }
    const msgs = localStorage.getItem("whatsapp_messages");
    if (msgs) {
      try { setCustomMessages(JSON.parse(msgs)); } catch { /* ignore */ }
    }
    const otp = localStorage.getItem("whatsapp_otp_template");
    if (otp) setOtpTemplate(otp);
    const nc = localStorage.getItem("whatsapp_new_customer_template");
    if (nc) setNewCustomerTemplate(nc);
  }, []);

  // Live OTP preview text
  const otpPreviewText = (otpTemplate || DEFAULT_OTP_TEMPLATE)
    .replace(/\{storeName\}/g, config.storeName || "المتجر")
    .replace(/\{otp\}/g, "٤٨٢٦١٩");

  const handleSave = async () => {
    setSaving(true);
    localStorage.setItem("whatsapp_settings", JSON.stringify(config));
    localStorage.setItem("whatsapp_messages", JSON.stringify(customMessages));
    if (otpTemplate.trim()) {
      localStorage.setItem("whatsapp_otp_template", otpTemplate.trim());
    } else {
      localStorage.removeItem("whatsapp_otp_template");
    }
    if (newCustomerTemplate.trim()) {
      localStorage.setItem("whatsapp_new_customer_template", newCustomerTemplate.trim());
    } else {
      localStorage.removeItem("whatsapp_new_customer_template");
    }
    setTimeout(() => {
      setSaving(false);
      onToast("تم حفظ إعدادات واتساب بنجاح");
    }, 400);
  };

  const handleTestSend = async (status: string) => {
    setTestingStatus(status);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-notify", {
        body: {
          type: "status_update",
          orderRef: "TEST001",
          phone: config.adminWhatsApp,
          name: "عميل تجريبي",
          city: "الرياض",
          status,
          items: [{ nameAr: "منتج تجريبي", qty: 1, price: 299 }],
          finalPrice: 299,
          paymentLabel: "الدفع عند الاستلام",
        },
      });

      if (error || !data?.success) {
        onToast("فشل الاختبار — تحقق من الإعدادات");
        return;
      }

      if (data.autoSent) {
        onToast(`تم إرسال رسالة اختبار "${STATUS_MESSAGES[status]?.label}" تلقائياً ✓`);
      } else {
        // Open WhatsApp manually
        if (data.whatsappUrl) window.open(data.whatsappUrl, "_blank", "noopener,noreferrer");
        onToast(`فُتح واتساب — أضف CALLMEBOT_API_KEY للإرسال التلقائي`);
      }
    } catch {
      onToast("حدث خطأ أثناء الاختبار");
    }
    setTestingStatus(null);
  };

  const DEFAULT_NEW_CUSTOMER_TEMPLATE = `🎉 *عميل جديد — {storeName}*\n\n👤 الاسم: *{name}*\n📱 الجوال: {phone}\n⏰ وقت التسجيل: {time}\n\n✅ تم تسجيله في قاعدة بيانات المتجر`;

  const newCustomerPreviewText = (newCustomerTemplate || DEFAULT_NEW_CUSTOMER_TEMPLATE)
    .replace(/\{storeName\}/g, config.storeName || "المتجر")
    .replace(/\{name\}/g, "أحمد محمد")
    .replace(/\{phone\}/g, "0512345678")
    .replace(/\{time\}/g, new Date().toLocaleString("ar-SA"));

  const handleTestNewCustomer = async () => {
    setTestingNewCustomer(true);
    try {
      const template = newCustomerTemplate.trim() || DEFAULT_NEW_CUSTOMER_TEMPLATE;
      const { data, error } = await supabase.functions.invoke("whatsapp-notify", {
        body: {
          type: "new_customer",
          name: "أحمد محمد (تجريبي)",
          phone: "0512345678",
          newCustomerTemplate: template,
        },
      });
      if (error || !data?.success) {
        onToast("فشل اختبار إشعار العميل الجديد — تحقق من الإعدادات");
        return;
      }
      if (data.autoSent) {
        onToast("تم إرسال إشعار العميل الجديد تلقائياً ✓");
      } else {
        if (data.whatsappUrl) window.open(data.whatsappUrl, "_blank", "noopener,noreferrer");
        onToast("فُتح واتساب — أضف CALLMEBOT_API_KEY للإرسال التلقائي");
      }
    } catch {
      onToast("حدث خطأ أثناء الاختبار");
    }
    setTestingNewCustomer(false);
  };

  const handleTestOtp = async () => {
    setTestingOtp(true);
    try {
      const testOtp = "482619";
      const template = otpTemplate.trim() || DEFAULT_OTP_TEMPLATE;
      const { data, error } = await supabase.functions.invoke("whatsapp-notify", {
        body: {
          type: "otp",
          phone: config.adminWhatsApp,
          otp: testOtp,
          otpTemplate: template,
        },
      });
      if (error || !data?.success) {
        onToast("فشل اختبار OTP — تحقق من الإعدادات");
        return;
      }
      if (data.autoSent) {
        onToast("تم إرسال رسالة OTP تجريبية تلقائياً ✓");
      } else {
        if (data.whatsappUrl) window.open(data.whatsappUrl, "_blank", "noopener,noreferrer");
        onToast("فُتح واتساب — أضف CALLMEBOT_API_KEY للإرسال التلقائي");
      }
    } catch {
      onToast("حدث خطأ أثناء اختبار OTP");
    }
    setTestingOtp(false);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
        <div className="w-12 h-12 flex items-center justify-center bg-green-500/10 rounded-xl">
          <i className="ri-whatsapp-line text-green-500 text-2xl"></i>
        </div>
        <div>
          <h3 className="font-bold text-[#1a1a1a]">إشعارات واتساب التلقائية</h3>
          <p className="text-gray-400 text-xs">إرسال رسائل تلقائية للعملاء عند كل تغيير في حالة الطلب</p>
        </div>
        <div className="mr-auto flex items-center gap-2">
          {config.callMeBotApiKey ? (
            <span className="flex items-center gap-1.5 text-xs font-bold text-green-600 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
              تلقائي مفعّل
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              يدوي فقط
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {[
          { key: "setup", label: "الإعداد", icon: "ri-settings-3-line" },
          { key: "messages", label: "قوالب الرسائل", icon: "ri-message-3-line" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as "setup" | "messages")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key ? "bg-white text-[#1a1a1a]" : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── SETUP TAB ── */}
      {activeTab === "setup" && (
        <div className="flex flex-col gap-5">

          {/* How it works */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-green-700 text-sm font-bold mb-2 flex items-center gap-2">
              <i className="ri-flashlight-line"></i>
              كيف يعمل الإرسال التلقائي؟
            </p>
            <ol className="text-green-700 text-xs space-y-1.5 list-decimal list-inside">
              <li>سجّل رقمك في <strong>CallMeBot</strong> (مجاني) عبر إرسال رسالة واتساب</li>
              <li>احصل على API Key وأضفه في الحقل أدناه</li>
              <li>أضف <strong>CALLMEBOT_API_KEY</strong> في Supabase Secrets</li>
              <li>عند تغيير حالة أي طلب، تُرسل رسالة واتساب تلقائياً للعميل</li>
            </ol>
            <a
              href="https://www.callmebot.com/blog/free-api-whatsapp-messages/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-green-700 text-xs font-bold hover:underline cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              شرح تفعيل CallMeBot
            </a>
          </div>

          {/* Store Name */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
            <label className="text-[#1a1a1a] text-sm font-bold mb-2 block">
              <i className="ri-store-2-line text-gray-500 ml-1"></i>
              اسم المتجر في الرسائل
            </label>
            <input
              type="text"
              value={config.storeName}
              onChange={(e) => setConfig({ ...config, storeName: e.target.value })}
              placeholder="مثال: EliteBazaar"
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-400 transition-colors"
            />
            <p className="text-gray-400 text-xs mt-1.5">يظهر في نهاية كل رسالة واتساب</p>
          </div>

          {/* Admin WhatsApp */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
            <label className="text-[#1a1a1a] text-sm font-bold mb-2 block">
              <i className="ri-whatsapp-line text-green-500 ml-1"></i>
              رقم واتساب الإدارة
            </label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🇸🇦 +966</span>
                <input
                  type="tel"
                  value={config.adminWhatsApp.replace(/^966/, "")}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 9);
                    setConfig({ ...config, adminWhatsApp: `966${val}` });
                  }}
                  placeholder="5XXXXXXXX"
                  className="w-full bg-white border border-gray-200 rounded-xl pr-24 pl-4 py-3 text-sm outline-none focus:border-green-400 transition-colors"
                />
              </div>
            </div>
            <p className="text-gray-400 text-xs mt-1.5">تصله إشعارات الطلبات الجديدة وتحديثات الحالة</p>
          </div>

          {/* CallMeBot API Key */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
            <label className="text-[#1a1a1a] text-sm font-bold mb-2 flex items-center gap-2">
              <i className="ri-key-2-line text-amber-500"></i>
              CallMeBot API Key
              <span className="text-[10px] font-normal text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">للإرسال التلقائي</span>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showApiKey ? "text" : "password"}
                  value={config.callMeBotApiKey}
                  onChange={(e) => setConfig({ ...config, callMeBotApiKey: e.target.value })}
                  placeholder="أدخل API Key من CallMeBot"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-400 transition-colors pr-12"
                />
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={`text-sm ${showApiKey ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                </button>
              </div>
            </div>
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              <p className="text-amber-700 text-xs font-bold mb-1">خطوات الحصول على API Key:</p>
              <ol className="text-amber-700 text-xs space-y-1 list-decimal list-inside">
                <li>أرسل رسالة واتساب لـ <strong>+34 644 59 78 26</strong></li>
                <li>اكتب: <code className="bg-amber-100 px-1 rounded">I allow callmebot to send me messages</code></li>
                <li>ستصلك رسالة تحتوي API Key الخاص بك</li>
              </ol>
            </div>
            <p className="text-gray-400 text-xs mt-2">
              <i className="ri-shield-check-line ml-1 text-green-500"></i>
              يجب إضافة هذا الـ Key في <strong>Supabase Secrets</strong> باسم <code className="bg-gray-200 px-1 rounded text-[11px]">CALLMEBOT_API_KEY</code>
            </p>
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { key: "customerNotificationsEnabled" as const, label: "إشعارات العملاء", desc: "رسائل تلقائية عند تغيير حالة الطلب", icon: "ri-user-line", color: "bg-green-500" },
              { key: "adminNotificationsEnabled" as const, label: "إشعارات الإدارة", desc: "إشعار الإدارة عند كل تحديث", icon: "ri-shield-user-line", color: "bg-gray-700" },
            ].map((toggle) => (
              <div key={toggle.key} className="bg-gray-50 rounded-xl p-4 border border-gray-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                    <i className={`${toggle.icon} text-gray-500 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-[#1a1a1a] text-sm font-bold">{toggle.label}</p>
                    <p className="text-gray-400 text-xs">{toggle.desc}</p>
                  </div>
                </div>
                <button
                  onClick={() => setConfig({ ...config, [toggle.key]: !config[toggle.key] })}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer flex-shrink-0 ${
                    config[toggle.key] ? toggle.color : "bg-gray-300"
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${
                    config[toggle.key] ? "left-6" : "left-1"
                  }`} />
                </button>
              </div>
            ))}
          </div>

          {/* Test Buttons */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
            <p className="text-[#1a1a1a] text-sm font-bold mb-3 flex items-center gap-2">
              <i className="ri-test-tube-line text-gray-500"></i>
              اختبار الإشعارات
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(STATUS_MESSAGES).map(([status, cfg]) => (
                <button
                  key={status}
                  onClick={() => handleTestSend(status)}
                  disabled={testingStatus !== null}
                  className="flex items-center gap-2 px-3 py-2.5 bg-white border border-gray-200 hover:border-green-300 hover:bg-green-50 rounded-xl text-xs font-bold text-gray-600 hover:text-green-700 transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {testingStatus === status ? (
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                  ) : (
                    <i className={`${cfg.icon} text-sm ${cfg.color}`}></i>
                  )}
                  {cfg.label}
                </button>
              ))}
            </div>
            <p className="text-gray-400 text-xs mt-3">
              سيُرسل اختبار لرقم الإدارة — إذا لم يكن CallMeBot مفعّلاً سيفتح واتساب يدوياً
            </p>
          </div>
        </div>
      )}

      {/* ── MESSAGES TAB ── */}
      {activeTab === "messages" && (
        <div className="flex flex-col gap-5">

          {/* Variables hint */}
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-amber-700 text-xs font-bold mb-1.5 flex items-center gap-1.5">
              <i className="ri-code-line"></i>
              المتغيرات المتاحة في رسائل الطلبات:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[
                { var: "{name}", desc: "اسم العميل" },
                { var: "{orderRef}", desc: "رقم الطلب" },
                { var: "{finalPrice}", desc: "المبلغ" },
                { var: "{status}", desc: "حالة الطلب" },
              ].map(v => (
                <span key={v.var} className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[11px] px-2 py-0.5 rounded-full">
                  <code className="font-mono font-bold">{v.var}</code>
                  <span className="text-amber-600">— {v.desc}</span>
                </span>
              ))}
            </div>
            <p className="text-amber-600 text-[11px] mt-2">اتركها فارغة لاستخدام الرسائل الافتراضية</p>
          </div>

          {/* ── OTP Message Template ── */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
                  <i className="ri-lock-password-line text-green-600 text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1a1a1a]">رسالة رمز التحقق (OTP)</p>
                  <p className="text-xs text-gray-400">تُرسل للعميل عند تسجيل الدخول برقم الجوال</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                  OTP
                </span>
              </div>
            </div>

            <div className="p-5 flex flex-col gap-4">
              {/* OTP Variables hint */}
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <p className="text-green-700 text-xs font-bold mb-1.5 flex items-center gap-1.5">
                  <i className="ri-code-line"></i>
                  المتغيرات الخاصة برسالة OTP:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { var: "{otp}", desc: "رمز التحقق المكوّن من 6 أرقام", required: true },
                    { var: "{storeName}", desc: "اسم المتجر" },
                  ].map(v => (
                    <span key={v.var} className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-[11px] px-2 py-0.5 rounded-full">
                      <code className="font-mono font-bold">{v.var}</code>
                      <span className="text-green-600">— {v.desc}</span>
                      {v.required && <span className="text-red-500 font-bold">*</span>}
                    </span>
                  ))}
                </div>
                <p className="text-green-600 text-[11px] mt-1.5">
                  <i className="ri-error-warning-line ml-0.5"></i>
                  <code className="font-mono">{"{otp}"}</code> مطلوب في الرسالة — بدونه لن يصل الرمز للعميل
                </p>
              </div>

              {/* Textarea */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-500">نص الرسالة</label>
                  <span className={`text-[11px] font-medium ${
                    (otpTemplate || DEFAULT_OTP_TEMPLATE).length > 450 ? "text-red-500" : "text-gray-400"
                  }`}>
                    {(otpTemplate || DEFAULT_OTP_TEMPLATE).length}/500
                  </span>
                </div>
                <textarea
                  value={otpTemplate}
                  onChange={(e) => setOtpTemplate(e.target.value.slice(0, 500))}
                  rows={5}
                  placeholder={DEFAULT_OTP_TEMPLATE}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-400 transition-colors resize-none"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                />
                {otpTemplate && !otpTemplate.includes("{otp}") && (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    تحذير: الرسالة لا تحتوي على <code className="bg-red-50 px-1 rounded font-mono">{"{otp}"}</code> — لن يصل الرمز للعميل!
                  </p>
                )}
              </div>

              {/* Preview & Actions row */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setOtpPreview(!otpPreview)}
                    className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-[#1a1a1a] transition-colors cursor-pointer"
                  >
                    <i className={`text-sm ${otpPreview ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                    {otpPreview ? "إخفاء المعاينة" : "معاينة الرسالة"}
                  </button>
                  {otpTemplate && (
                    <button
                      onClick={() => setOtpTemplate("")}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-refresh-line text-sm"></i>
                      استعادة الافتراضية
                    </button>
                  )}
                </div>
                <button
                  onClick={handleTestOtp}
                  disabled={testingOtp}
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  {testingOtp ? (
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                  ) : (
                    <i className="ri-send-plane-line text-sm"></i>
                  )}
                  إرسال اختبار لرقم الإدارة
                </button>
              </div>

              {/* Preview bubble */}
              {otpPreview && (
                <div className="bg-[#0f0f0f] rounded-2xl p-4">
                  <p className="text-gray-500 text-[10px] mb-3 flex items-center gap-1.5">
                    <i className="ri-whatsapp-line text-green-500"></i>
                    معاينة الرسالة كما ستظهر في واتساب
                  </p>
                  <div className="bg-[#1a1a1a] rounded-2xl rounded-tr-sm px-4 py-3 max-w-xs">
                    <p
                      className="text-white text-xs leading-relaxed whitespace-pre-wrap"
                      style={{ fontFamily: "'Cairo', sans-serif" }}
                    >
                      {otpPreviewText}
                    </p>
                    <p className="text-gray-500 text-[10px] mt-2 text-left">
                      {new Date().toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── New Customer Notification Template ── */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-user-add-line text-amber-600 text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1a1a1a]">إشعار تسجيل عميل جديد</p>
                  <p className="text-xs text-gray-400">تُرسل للأدمن فور تسجيل أي عميل جديد في المتجر</p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                للأدمن
              </span>
            </div>

            <div className="p-5 flex flex-col gap-4">
              {/* Variables hint */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-amber-700 text-xs font-bold mb-1.5 flex items-center gap-1.5">
                  <i className="ri-code-line"></i>
                  المتغيرات المتاحة:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { var: "{name}", desc: "اسم العميل" },
                    { var: "{phone}", desc: "رقم الجوال" },
                    { var: "{storeName}", desc: "اسم المتجر" },
                    { var: "{time}", desc: "وقت التسجيل" },
                  ].map(v => (
                    <span key={v.var} className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[11px] px-2 py-0.5 rounded-full">
                      <code className="font-mono font-bold">{v.var}</code>
                      <span className="text-amber-600">— {v.desc}</span>
                    </span>
                  ))}
                </div>
                <p className="text-amber-600 text-[11px] mt-1.5">اتركها فارغة لاستخدام الرسالة الافتراضية</p>
              </div>

              {/* Textarea */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-500">نص الرسالة</label>
                  <span className={`text-[11px] font-medium ${
                    (newCustomerTemplate || DEFAULT_NEW_CUSTOMER_TEMPLATE).length > 450 ? "text-red-500" : "text-gray-400"
                  }`}>
                    {(newCustomerTemplate || DEFAULT_NEW_CUSTOMER_TEMPLATE).length}/500
                  </span>
                </div>
                <textarea
                  value={newCustomerTemplate}
                  onChange={(e) => setNewCustomerTemplate(e.target.value.slice(0, 500))}
                  rows={5}
                  placeholder={DEFAULT_NEW_CUSTOMER_TEMPLATE}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                />
              </div>

              {/* Preview & Actions row */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setNewCustomerPreview(!newCustomerPreview)}
                    className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-[#1a1a1a] transition-colors cursor-pointer"
                  >
                    <i className={`text-sm ${newCustomerPreview ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                    {newCustomerPreview ? "إخفاء المعاينة" : "معاينة الرسالة"}
                  </button>
                  {newCustomerTemplate && (
                    <button
                      onClick={() => setNewCustomerTemplate("")}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-refresh-line text-sm"></i>
                      استعادة الافتراضية
                    </button>
                  )}
                </div>
                <button
                  onClick={handleTestNewCustomer}
                  disabled={testingNewCustomer}
                  className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  {testingNewCustomer ? (
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                  ) : (
                    <i className="ri-send-plane-line text-sm"></i>
                  )}
                  إرسال اختبار لرقم الإدارة
                </button>
              </div>

              {/* Preview bubble */}
              {newCustomerPreview && (
                <div className="bg-[#0f0f0f] rounded-2xl p-4">
                  <p className="text-gray-500 text-[10px] mb-3 flex items-center gap-1.5">
                    <i className="ri-whatsapp-line text-green-500"></i>
                    معاينة الرسالة كما ستظهر في واتساب
                  </p>
                  <div className="bg-[#1a1a1a] rounded-2xl rounded-tr-sm px-4 py-3 max-w-xs">
                    <p
                      className="text-white text-xs leading-relaxed whitespace-pre-wrap"
                      style={{ fontFamily: "'Cairo', sans-serif" }}
                    >
                      {newCustomerPreviewText}
                    </p>
                    <p className="text-gray-500 text-[10px] mt-2 text-left">
                      {new Date().toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-400 font-medium">رسائل حالات الطلبات</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Order Status Messages */}
          {Object.entries(STATUS_MESSAGES).map(([status, cfg]) => (
            <div key={status} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <label className={`text-sm font-bold mb-3 flex items-center gap-2 ${cfg.color}`}>
                <i className={`${cfg.icon} text-base`}></i>
                {cfg.label}
              </label>
              <textarea
                value={customMessages[status] ?? ""}
                onChange={(e) => setCustomMessages({ ...customMessages, [status]: e.target.value })}
                rows={3}
                placeholder={cfg.defaultMsg}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-400 transition-colors resize-none"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              />
              {customMessages[status] && (
                <button
                  onClick={() => {
                    const updated = { ...customMessages };
                    delete updated[status];
                    setCustomMessages(updated);
                  }}
                  className="mt-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line ml-1"></i>
                  استعادة الرسالة الافتراضية
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Save */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-black disabled:opacity-50 text-white font-bold py-3 px-6 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          {saving ? (
            <><i className="ri-loader-4-line animate-spin"></i>جاري الحفظ...</>
          ) : (
            <><i className="ri-save-line"></i>حفظ الإعدادات</>
          )}
        </button>
      </div>
    </div>
  );
}
